import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Dropdown,
  Grid,
  Radio,
  RadioGroup,
  TextInput,
  Typography,
  styled,
  Button,
  Checkbox,
} from '@deliveryhero/armor'

import { config } from '../../config/global'
import { Field } from '../../global/types'

import { DenormalizedLandingPage } from './selectors'
import { convertRulesToCities } from './utils'

const VerticalGrid = styled(Grid)`
  flex-direction: column;

  & > * {
    margin-bottom: 36px;
  }
`

const FormTitle = styled(Typography)`
  color: #fff;
  margin-bottom: 60px;
  font-size 25px;
`

const LoginButton = styled(Button)`
  color: ${props => props.color};
  width: 48%;
  margin-right: 4%;
`

const SubmitButton = styled(Button)`
  background-color: ${props => props.background};
  color: ${props => props.color};
  width: ${config.loginEnabled ? '48%' : '100%'};
`

export const FormGenerator: React.FC<{
  landingPage: DenormalizedLandingPage
  activeLandingPageContentIdx: number
  data: Record<string, string>
  onChange: (e) => void
  onSubmit: (data: Record<string, string>) => void
  previewMode: boolean
}> = ({
  landingPage,
  activeLandingPageContentIdx,
  data = {},
  onChange,
  onSubmit,
  previewMode,
}) => {
  const [t] = useTranslation()

  const landingPageContent =
    landingPage.landingPageContents[activeLandingPageContentIdx]

  if (!landingPageContent.workflow) return null

  const cities = convertRulesToCities(landingPageContent.workflow.rules).map(
    city => ({
      value: city,
      label: city,
    }),
  )

  const vehicles = data.city
    ? landingPageContent.vehicles
        .filter(vehicle => vehicle.cities.includes(data.city))
        .map(vehicle => ({ value: vehicle.name, label: vehicle.text }))
    : []

  const radioComponentsText = {
    age: (minAge: number) =>
      t('FormGenerator.AreYouOver').replace('$AGE', minAge.toString()),
  }

  const mapping = {
    string: (field: Field, index: number) => (
      <TextInput
        name={field.name}
        label={field.text}
        value={data[field.name]}
        onChange={onChange}
        autoFocus={index === 0}
      />
    ),
    radio: (field: Field, index: number) => (
      <section>
        <Typography subSectionTitle color="#fff">
          {field.name === 'age'
            ? radioComponentsText[field.name](field.text)
            : field.text}
        </Typography>
        <RadioGroup
          name={field.name}
          selectedValue={data[field.name]}
          onChange={onChange}
          autoFocus={index === 0}
          typographyProps={{ large: false, color: '#fff !important' }}
        >
          {field.settings.options.map(option => (
            <Radio {...option} label={t(option.label)} />
          ))}
        </RadioGroup>
      </section>
    ),
  }

  const fields = landingPageContent.fields
    .filter(field => field.enabled)
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((field, index) => mapping[field.fieldType](field, index))

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (onSubmit) {
      onSubmit(data)
    }
  }

  const loginUrl = config.loginUrlTemplate.replace(
    '$',
    landingPage?.country?.id,
  )

  return (
    <>
      <form onSubmit={handleSubmit}>
        <VerticalGrid direction="column">
          <FormTitle sectionTitle>
            {t('LandingPage.CreateYourProfile')}
          </FormTitle>
          <Dropdown
            name="city"
            placeholder={t('LandingPage.ChooseYourCity')}
            value={data.city}
            options={cities}
            onChange={onChange}
          />
          <Dropdown
            name="vehicle"
            placeholder={t('LandingPage.ChooseYourVehicle')}
            disabled={!data.city}
            onChange={onChange}
            value={data.vehicle}
            options={vehicles}
          />
          {data.vehicle && (
            <>
              {fields}

              {landingPage.privacyPolicyUrl &&
                landingPage.privacyPolicyUrl !== '' && (
                  <section>
                    <Checkbox
                      value={data.privacyPolicyChecked}
                      onChange={e =>
                        onChange({
                          target: {
                            name: 'privacyPolicyChecked',
                            value: e.target.checked,
                          },
                        })
                      }
                      label={
                        <Typography paragraph medium color="#fff">
                          {t('LandingPage.PrivacyPolicyText')}

                          <a
                            rel="noreferrer noopener"
                            target="_blank"
                            href={landingPage.privacyPolicyUrl}
                          >
                            <Typography paragraph medium color="#fff">
                              {t('LandingPage.PrivacyPolicyTextLink')}
                            </Typography>
                          </a>
                        </Typography>
                      }
                    />
                  </section>
                )}
            </>
          )}

          <div>
            {config.loginEnabled && (
              <LoginButton
                tag="a"
                href={loginUrl}
                tertiary
                color={landingPage.theme.secondaryColor}
              >
                {t('LandingPage.SignIn')}
              </LoginButton>
            )}

            <SubmitButton
              type="submit"
              disabled={
                previewMode ||
                (landingPage.privacyPolicyUrl && !data.privacyPolicyChecked)
              }
              tertiary
              color={landingPage.theme.primaryColor}
              background={landingPage.theme.secondaryColor}
            >
              {t('LandingPage.Submit')}
            </SubmitButton>
          </div>
        </VerticalGrid>
      </form>
    </>
  )
}
